import { useCallback, useEffect, useState } from 'react'
import { message } from 'antd'
import { handleError } from '../../../../lib/utils'
import { countRemittances, createRemittance, getRemittances, updateRemittance } from '../utils'
import dayjs from 'dayjs'
import { debounce } from 'lodash'
import { MedPraxSchemeSearch } from '../../../../Scripting/utils/utils'
import { useSelector } from 'react-redux'

/**
 * Hook for managing medical aid payment list
 * @returns {Object} Functions and state for medical aid payment list
 */
export const useMedicalAidPaymentList = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({
    status: '',
    invoiceType: '',
    createdAt: '',
    search: ''
  })

  const [remittanceLoading, setRemittanceLoading] = useState(false)
  const [remittanceModalVisible, setRemittanceModalVisible] = useState(false)

  const [schemeData, setSchemeData] = useState([])
  const [schemeLoading, setSchemeLoading] = useState(false)

  const agiliteUser = useSelector((state) => state.auth.agiliteUser)

  useEffect(() => {
    handleCountRemittances()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    handleGetRemittances()
    // eslint-disable-next-line
  }, [filters, page, pageSize])

  /**
   * Counts total remittances based on filters
   */
  const handleCountRemittances = async () => {
    let tmpTotal = 0

    try {
      tmpTotal = await countRemittances()
      // Extract the count value from the response
      const totalCount = typeof tmpTotal === 'object' ? tmpTotal.result || tmpTotal.count || 0 : tmpTotal
      setTotal(totalCount)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Gets remittances based on current filters and pagination
   */
  const handleGetRemittances = async () => {
    let tmpData = []
    let tmpTotal = 0
    let tmpFilters = JSON.parse(JSON.stringify(filters))

    setLoading(true)

    try {
      // Filters
      for (const key in tmpFilters) {
        if (!tmpFilters[key]) {
          delete tmpFilters[key]
        }

        if (key === 'createdAt' && tmpFilters[key]) {
          tmpFilters[key] = {
            $lte: dayjs(new Date(tmpFilters[key])).set('hour', 23).set('minute', 59).set('second', 59).toDate(),
            $gte: dayjs(new Date(tmpFilters[key])).set('hour', 0).set('minute', 0).set('second', 0).toDate()
          }
        }

        if (key === 'search' && tmpFilters[key]) {
          tmpFilters.$or = [
            { receiptNumber: { $regex: tmpFilters[key], $options: 'i' } },
            { medicalAid: { $regex: tmpFilters[key], $options: 'i' } }
          ]
          delete tmpFilters[key]
        }
      }

      tmpTotal = await countRemittances(tmpFilters)
      // Extract the count value from the response
      const totalCount = typeof tmpTotal === 'object' ? tmpTotal.result || tmpTotal.count || 0 : tmpTotal
      setTotal(totalCount)

      tmpData = await getRemittances(tmpFilters, page, pageSize)

      setData(tmpData)
    } catch (e) {
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  /**
   * Finalizes a remittance
   * @param {Object} record - Remittance record to finalize
   */
  const handleFinalizeRemittance = async (record) => {
    try {
      await updateRemittance(record._id, { finalized: true, finalizedDate: new Date() })
      handleGetRemittances()
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Saves a new remittance
   * @param {Object} formValues - Form values from remittance form
   * @param {Function} formInstance - Form instance for resetting fields
   * @param {Function} onSuccess - Callback on successful save
   */
  const handleSaveRemittance = async (formValues, formInstance, onSuccess) => {
    let tmpData = null

    setRemittanceLoading(true)

    try {
      tmpData = await createRemittance({
        ...formValues,
        createdAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        createdBy: agiliteUser._id,
        finalized: false
      })

      if (formInstance) {
        formInstance.resetFields()
      }

      setRemittanceModalVisible(false)

      if (onSuccess) {
        onSuccess(tmpData)
      }

      return tmpData
    } catch (e) {
      message.error(handleError(e, true))
      return null
    } finally {
      setRemittanceLoading(false)
    }
  }

  /**
   * Searches for medical aid schemes
   * @param {string} query - Search query
   */
  const handleGetMedicalAids = async (query) => {
    let tmpData = []

    setSchemeLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemeSearch(query)
        setSchemeData(tmpData)
      } else {
        setSchemeData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setSchemeLoading(false)
  }

  /**
   * Debounced function for scheme filter
   */
  const debouncedSchemeFilter = useCallback(
    debounce((query) => {
      if (query) {
        handleGetMedicalAids(query)
      } else {
        setSchemeLoading(false)
      }
    }, 1000),
    []
  )

  return {
    loading,
    data,
    page,
    pageSize,
    total,
    filters,
    remittanceLoading,
    remittanceModalVisible,
    schemeData,
    schemeLoading,
    setPage,
    setPageSize,
    setFilters,
    setRemittanceModalVisible,
    setSchemeData,
    handleCountRemittances,
    handleGetRemittances,
    handleFinalizeRemittance,
    handleSaveRemittance,
    debouncedSchemeFilter
  }
}
